import React, { useState, useEffect } from 'react';
import { Table, Button, Card, CardHeader, CardBody } from 'reactstrap';
import NewServiceModal from './NewServiceModal';
import EditServiceModal from './EditServiceModal';
import api from '../services/api';
import Layout from './Layout';

const ServiceManagement = () => {
    const [services, setServices] = useState([]);
    const [organization, setOrganization] = useState(null);
    const [newService, setNewService] = useState({
        title: '',
        description: '',
        duration: 60,
        color_code: 'primary',
        sort: 99,
        internal: true,
        resource_type: '',
        resource_count: 1,
        payment_option: 'None',
        valid_from: new Date('1/1/2000'),
        valid_to: new Date('12/31/9999')
    });
    const [editService, setEditService] = useState(null);
    const [newModalOpen, setNewModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [resourceTypes, setResourceTypes] = useState([]);
    const [resources, setResources] = useState([]);

    useEffect(() => {
        fetchServices();
        fetchResourceTypes();
        fetchOrganization();
        fetchResources();
    }, []);

    const fetchServices = async () => {
        try {
            const response = await api.get('/services/');
            setServices(response.data);
        } catch (error) {
            console.error('Error fetching services:', error);
        }
    };

    const fetchResourceTypes = async () => {
        try {
            const response = await api.get('/resource-types/');
            setResourceTypes(response.data);
        } catch (error) {
            console.error('Error fetching resource types:', error);
        }
    };

    const fetchOrganization = async () => {
        try {
            const response = await api.get('/get-org-details/');
            setOrganization(response.data);
        } catch (error) {
            console.error('Error fetching organization:', error);
        }
    };

    const fetchResources = async () => {
        try {
            const response = await api.get('/resources/');
            setResources(response.data);
        } catch (error) {
            console.error('Error fetching resources:', error);
        }
    };

    const addService = async () => {
        try {
            const validFrom = `${newService.valid_from.getFullYear()}-${String(newService.valid_from.getMonth() + 1).padStart(2, '0')}-${String(newService.valid_from.getDate()).padStart(2, '0')}`;
            const validTo = `${newService.valid_to.getFullYear()}-${String(newService.valid_to.getMonth() + 1).padStart(2, '0')}-${String(newService.valid_to.getDate()).padStart(2, '0')}`;

            await api.post('/services/', { ...newService, valid_from: validFrom, valid_to: validTo });
            fetchServices();

            // Reset the form and close the modal
            setNewService({
                title: '',
                description: '',
                duration: 60,
                color_code: 'primary',
                sort: 99,
                internal: true,
                resource_type: '',
                resource_count: 1,
                payment_option: 'None',
                valid_from: new Date('1/1/2000'),
                valid_to: new Date('12/31/9999')
            });

            setNewModalOpen(false);
        } catch (error) {
            console.error('Error adding service:', error);
        }
    };

    const deleteService = async (id) => {
        try {
            await api.delete(`/services/${id}/`);
            fetchServices();
        } catch (error) {
            console.error('Error deleting service:', error);
        }
    };

    const openEditModal = (service) => {
        setEditService(service);
        setEditModalOpen(true);
    };

    const editServiceData = async () => {
        try {
            await api.put(`/services/${editService.id}/`, editService);
            fetchServices();
            setEditModalOpen(false);
        } catch (error) {
            console.error('Error editing service:', error);
        }
    };

    return (
        <Layout>
            <div style={{ padding: '1rem' }}>
                <Card>
                    <CardHeader tag="h3">Services</CardHeader>
                    <CardBody>
                        <Table striped hover>
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th>Duration (min)</th>
                                    <th>Resource Type</th>
                                    <th>#</th>
                                    <th>Color</th>
                                    <th>Payment Option</th>
                                    <th>Sort</th>
                                    <th>Internal</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {services.map(service => {
                                    const resourceType = resourceTypes.find(type => type.id === service.resource_type);
                                    return (
                                        <tr key={service.id}>
                                            <td>{service.title}</td>
                                            <td>{service.duration}</td>
                                            <td>{resourceType?.name || service.resource_type || 'N/A'}</td>
                                            <td>{service.resource_count}</td>
                                            <td>
                                                <div style={{
                                                    height: '40px',
                                                    aspectRatio: '1/1',
                                                    backgroundColor: service.color_code,
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px'
                                                }} />
                                            </td>
                                            <td>
                                                <span className={`badge bg-${service.payment_option === 'PrePaid' ? 'success' : service.payment_option === 'Hold' ? 'warning' : 'secondary'}`}>
                                                    {service.payment_option || 'None'}
                                                </span>
                                            </td>
                                            <td>{service.sort}</td>
                                            <td>{service.internal ? 'Yes' : 'No'}</td>
                                            <td>
                                                <Button color="primary" onClick={() => openEditModal(service)}>
                                                    Edit
                                                </Button>
                                                {' '}
                                                <Button color="danger" onClick={() => deleteService(service.id)}>
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>

                    </CardBody>
                </Card>

                <Button color="primary" onClick={() => setNewModalOpen(true)}>
                    Add Service
                </Button>

                {newModalOpen && (
                    <NewServiceModal
                        isOpen={newModalOpen}
                        toggle={() => setNewModalOpen(!newModalOpen)}
                        onSave={addService}
                        service={newService}
                        onInputChange={(name, value) => setNewService(prevState => ({ ...prevState, [name]: value }))}
                        resourceTypes={resourceTypes}
                        resources={resources}
                        organization={organization}
                    />
                )}

                {editService && editModalOpen && (
                    <EditServiceModal
                        isOpen={editModalOpen}
                        toggle={() => setEditModalOpen(!editModalOpen)}
                        onSave={editServiceData}
                        service={editService}
                        onInputChange={(name, value) => setEditService(prevState => ({ ...prevState, [name]: value }))}
                        resourceTypes={resourceTypes}
                        resources={resources}
                        organization={organization}
                    />
                )}
            </div>
        </Layout>
    );
};

export default ServiceManagement;